import React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";
import { Building2, LogIn, UserPlus, LogOut, LayoutDashboard, BookOpen, Globe, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";

function NavItem({ to, children }) {
    return (
        <NavLink
            to={to}
            className={({ isActive }) =>
                cn(
                    "text-sm transition-colors",
                    isActive ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground",
                )
            }
        >
            {children}
        </NavLink>
    );
}

export function PublicLayout() {
    const { user, logout } = useAuth();
    const location = useLocation();
    const loginOnly = process.env.REACT_APP_PUBLIC_LOGIN_ONLY === "true";
    const isLoginRegister = location.pathname === "/login" || location.pathname === "/register";

    return (
        <div className="min-h-screen bg-background text-foreground flex flex-col">
            <header className="border-b border-border bg-card/80 backdrop-blur sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="public-logo">
                        <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground grid place-items-center">
                            <Building2 className="w-4 h-4" />
                        </div>
                        <div className="font-semibold tracking-tight">HotelOS</div>
                    </Link>
                    {!loginOnly && (
                        <nav className="hidden md:flex items-center gap-6">
                            <NavItem to="/">Home</NavItem>
                            <NavItem to="/book"><Globe className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Book a stay</NavItem>
                            <NavItem to="/chat"><MessageSquare className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Concierge</NavItem>
                            <NavItem to="/blog"><BookOpen className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Blog</NavItem>
                        </nav>
                    )}
                    <div className="flex items-center gap-2">
                        {user ? (
                            <>
                                <Button asChild size="sm" variant="outline" data-testid="public-open-admin">
                                    <Link to="/admin/dashboard">
                                        <LayoutDashboard className="w-3.5 h-3.5 mr-1" /> Open admin
                                    </Link>
                                </Button>
                                <Button size="sm" variant="ghost" onClick={logout} data-testid="public-logout">
                                    <LogOut className="w-3.5 h-3.5 mr-1" /> Logout
                                </Button>
                            </>
                        ) : loginOnly || !isLoginRegister ? (
                            <>
                                <Button asChild size="sm" variant="ghost" data-testid="public-login-btn">
                                    <Link to="/login">
                                        <LogIn className="w-3.5 h-3.5 mr-1" /> Login
                                    </Link>
                                </Button>
                                {!loginOnly && (
                                    <Button asChild size="sm" data-testid="public-register-btn">
                                        <Link to="/register">
                                            <UserPlus className="w-3.5 h-3.5 mr-1" /> Get started
                                        </Link>
                                    </Button>
                                )}
                            </>
                        ) : null}
                    </div>
                </div>
            </header>
            <main className="flex-1">
                <Outlet />
            </main>
            {!loginOnly && (
                <footer className="border-t border-border bg-card mt-12">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
                        <div>© {new Date().getFullYear()} HotelOS — self-hosted hotel operations platform.</div>
                        <div className="flex items-center gap-4">
                            <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">GitHub</a>
                            <Link to="/blog" className="hover:text-foreground">Blog</Link>
                            <Link to="/admin/strategy" className="hover:text-foreground">Architecture</Link>
                        </div>
                    </div>
                </footer>
            )}
        </div>
    );
}
